// src/pages/MonthlyReportPage.js
import React, { useState } from 'react';
import { 
  Card, 
  Button, 
  DatePicker, 
  Spin, 
  Row, 
  Col, 
  Statistic, 
  Table, 
  Tag, 
  Alert,
  message,
  Divider
} from 'antd';
import { 
  DownloadOutlined, 
  FileTextOutlined,
  RiseOutlined,
  FallOutlined,
  CloudOutlined,
  SunOutlined,
  ThunderboltOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import reportService from '../services/reportService';

const { RangePicker } = DatePicker;

const MonthlyReportPage = () => {
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [dateRange, setDateRange] = useState([
    dayjs('2025-11-14'),
    dayjs('2025-11-29')
  ]);

  const calculateWeatherCorrelation = (dailyBreakdown, weatherDays) => {
    const rainyDays = { days: 0, totalSales: 0, transactions: 0, avgSales: 0 };
    const clearDays = { days: 0, totalSales: 0, transactions: 0, avgSales: 0 };
    
    dailyBreakdown.forEach(day => {
      // Format the sales date to YYYY-MM-DD for comparison
      const salesDate = dayjs(day.date).format('YYYY-MM-DD');
      
      // Find matching weather day
      const weatherDay = weatherDays.find(w => w.date === salesDate);
      
      if (weatherDay) {
        if (weatherDay.isRainy) {
          rainyDays.days++;
          rainyDays.totalSales += parseFloat(day.sales);
          rainyDays.transactions += day.invoices;
        } else {
          clearDays.days++;
          clearDays.totalSales += parseFloat(day.sales);
          clearDays.transactions += day.invoices;
        }
      }
    });
    
    // Calculate averages
    rainyDays.avgSales = rainyDays.days > 0 ? rainyDays.totalSales / rainyDays.days : 0;
    clearDays.avgSales = clearDays.days > 0 ? clearDays.totalSales / clearDays.days : 0;
    
    // Calculate impact
    const impactPct = clearDays.avgSales > 0 
      ? ((rainyDays.avgSales - clearDays.avgSales) / clearDays.avgSales * 100)
      : 0;
    
    return {
      rainyDays,
      clearDays,
      impact: {
        percentage: impactPct.toFixed(1),
        interpretation: impactPct < -20 ? "Las ventas disminuyeron significativamente en días lluviosos" :
                       impactPct > 20 ? "Las ventas aumentaron en días lluviosos" :
                       "No hay impacto negativo"
      }
    };
  };

  const generateReport = async () => {
    setLoading(true);
    try {
      const [start, end] = dateRange;
      
      const result = await reportService.getMonthlyReport(
        start.format('YYYY-MM-DD'),
        end.format('YYYY-MM-DD')
      );
      
      if (result.success) {
        // Fix weather correlation if backend returns zeros
        if (result.data.weatherCorrelation.rainyDays.days === 0 && 
            result.data.weatherCorrelation.clearDays.days === 0) {
          console.log('Backend weather correlation is empty, calculating in frontend...');
          result.data.weatherCorrelation = calculateWeatherCorrelation(
            result.data.dailyBreakdown,
            result.data.weather.days
          );
        }
        
        setReport(result.data);
        message.success('¡Reporte generado exitosamente!');
      } else {
        message.error('Error al generar el reporte');
      }
    } catch (error) {
      console.error('Error generating report:', error);
      message.error('Error al generar el reporte: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const downloadReport = () => {
    if (report) {
      const filename = `reporte-mensual-${report.period.start}-a-${report.period.end}.json`;
      reportService.downloadJSON(report, filename);
      message.success('¡Reporte descargado!');
    }
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 95) return 'green';
    if (confidence >= 80) return 'blue';
    if (confidence >= 60) return 'orange';
    return 'red';
  };

  const getPriorityColor = (priority) => {
    switch(priority) {
      case 'CRITICAL': return 'red';
      case 'HIGH': return 'orange';
      case 'MEDIUM': return 'blue';
      case 'LOW': return 'default';
      default: return 'default';
    }
  };

  // Payment method code dictionary (AFIP/ARCA codes)
  const getPaymentMethodName = (code) => {
    const paymentMethods = {
      '201': 'Contado',
      '202': 'Tarjeta de Débito',
      '203': 'Tarjeta de Crédito',
      '204': 'Cheque',
      '205': 'Ticket',
      '206': 'Cuenta Corriente',
      '207': 'Transferencia Bancaria',
      '208': 'Billetera Electrónica',
      '209': 'Mercado Pago',
      '210': 'Otro'
    };
    return paymentMethods[code] || code;
  };

  // Translate confidence level to Spanish
  const translateConfidenceLevel = (level) => {
    const translations = {
      'CERTAIN': 'CERTERO',
      'HIGH': 'ALTO',
      'MEDIUM': 'MEDIO',
      'LOW': 'BAJO',
      'SPECULATIVE': 'ESPECULATIVO'
    };
    return translations[level] || level;
  };

  // Translate priority to Spanish
  const translatePriority = (priority) => {
    const translations = {
      'CRITICAL': 'CRÍTICO',
      'HIGH': 'ALTO',
      'MEDIUM': 'MEDIO',
      'LOW': 'BAJO'
    };
    return translations[priority] || priority;
  };

  // Translate insight type to Spanish
  const translateInsightType = (type) => {
    const translations = {
      'Sales Performance': 'Rendimiento de Ventas',
      'Weather Impact': 'Impacto del Clima',
      'Payment Preferences': 'Preferencias de Pago',
      'Product Performance': 'Rendimiento de Productos',
      'Daily Pattern': 'Patrón Diario'
    };
    return translations[type] || type;
  };

  // Translate insight text to Spanish
  const translateInsightText = (text) => {
    // Replace common English phrases with Spanish
    return text
      .replace(/Generated \$([0-9,.]+) in revenue from ([0-9]+) invoices over ([0-9]+) days\./i, 
               'Se generaron $$$1 en ventas desde $2 facturas durante $3 días.')
      .replace(/is the dominant payment method \(([0-9.]+)% of revenue\)\./i,
               'es el método de pago dominante ($1% de los ingresos).')
      .replace(/([0-9]+) is the dominant payment method/i, 
               '$1 es el método de pago dominante');
  };

  // Translate recommendation fields to Spanish
  const translateRecommendation = (rec) => {
    const translated = { ...rec };
    
    // Translate title
    const titleTranslations = {
      'Start tracking bank promotions': 'Comenzar a rastrear promociones bancarias',
      'Low card payment adoption': 'Baja adopción de pagos con tarjeta'
    };
    translated.title = titleTranslations[rec.title] || rec.title;
    
    // Translate action
    translated.action = rec.action
      .replace(/Begin manual tracking of bank promotion calendar/i, 
               'Comenzar seguimiento manual del calendario de promociones bancarias')
      .replace(/Promote card payments and bank promotions/i,
               'Promover pagos con tarjeta y promociones bancarias');
    
    // Translate expected impact
    translated.expectedImpact = rec.expectedImpact
      .replace(/\+([0-9-]+)% sales on promotion days \(proven\)/i,
               '+$1% de ventas en días de promoción (comprobado)')
      .replace(/Increasing card usage to ([0-9]+)%\+ can boost average ticket by ([0-9-]+)%/i,
               'Aumentar el uso de tarjetas a $1%+ puede incrementar el ticket promedio en $2%');
    
    // Translate effort
    const effortTranslations = {
      'Very Low (15 min/week)': 'Muy Bajo (15 min/semana)',
      'Low (staff training + signage)': 'Bajo (capacitación del personal + señalización)'
    };
    translated.effort = effortTranslations[rec.effort] || rec.effort;
    
    return translated;
  };

  return (
    <div style={{ padding: 24, maxWidth: 1400, margin: '0 auto' }}>
      <h1>
        <FileTextOutlined /> Reporte Mensual de Negocio
      </h1>
      
      {/* Date Selection */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={16} align="middle">
          <Col>
            <label style={{ marginRight: 8, fontWeight: 'bold' }}>Período:</label>
          </Col>
          <Col>
            <RangePicker
              value={dateRange}
              onChange={setDateRange}
              format="DD/MM/YYYY"
              style={{ width: 280 }}
              disabled={loading}
            />
          </Col>
          <Col>
            <Button
              type="primary"
              onClick={generateReport}
              loading={loading}
              icon={<FileTextOutlined />}
              size="large"
            >
              Generar Reporte
            </Button>
          </Col>
          {report && (
            <Col>
              <Button
                icon={<DownloadOutlined />}
                onClick={downloadReport}
                size="large"
              >
                Descargar JSON
              </Button>
            </Col>
          )}
        </Row>
      </Card>

      {/* Loading */}
      {loading && (
        <div style={{ textAlign: 'center', padding: 60 }}>
          <Spin size="large" />
          <p style={{ marginTop: 16, fontSize: 16 }}>Generando reporte...</p>
          <p style={{ color: '#666' }}>Obteniendo datos de ventas y clima...</p>
        </div>
      )}

      {/* Report Display */}
      {!loading && report && (
        <>
          {/* Header Info */}
          <Alert
            message={`Reporte generado para ${report.period.start} a ${report.period.end} (${report.period.days} días)`}
            description={`${report.location} - Generado el ${dayjs(report.generatedAt).format('DD/MM/YYYY HH:mm')}`}
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
          />

          {/* Summary Stats */}
          <Card title="📊 Resumen Ejecutivo" style={{ marginBottom: 24 }}>
            <Row gutter={16}>
              <Col xs={24} sm={12} lg={6}>
                <Statistic
                  title="Ventas Totales"
                  value={parseFloat(report.salesSummary.totalSales)}
                  prefix="$"
                  precision={0}
                  valueStyle={{ color: '#3f8600' }}
                />
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Statistic
                  title="Facturas Emitidas"
                  value={report.salesSummary.totalInvoices}
                  suffix="facturas"
                />
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Statistic
                  title="Ticket Promedio"
                  value={parseFloat(report.salesSummary.avgTicket)}
                  prefix="$"
                  precision={0}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Statistic
                  title="Promedio Diario"
                  value={parseFloat(report.salesSummary.avgDailySales)}
                  prefix="$"
                  precision={0}
                />
              </Col>
            </Row>
          </Card>

          {/* Top Products */}
          <Card title="🏆 Top 10 Productos" style={{ marginBottom: 24 }}>
            <Table
              dataSource={report.topProducts}
              rowKey="product_code"
              pagination={false}
              size="small"
              scroll={{ x: 800 }}
              columns={[
                {
                  title: '#',
                  render: (_, __, index) => index + 1,
                  width: 50,
                  align: 'center'
                },
                {
                  title: 'Producto',
                  dataIndex: 'ProductName',
                  key: 'ProductName',
                  ellipsis: true
                },
                {
                  title: 'Código',
                  dataIndex: 'product_code',
                  key: 'product_code',
                  width: 120
                },
                {
                  title: 'Unidades',
                  dataIndex: 'totalUnits',
                  key: 'totalUnits',
                  align: 'right',
                  width: 100
                },
                {
                  title: 'Veces Vendido',
                  dataIndex: 'timesSold',
                  key: 'timesSold',
                  align: 'right',
                  width: 120
                },
                {
                  title: 'Ingresos',
                  dataIndex: 'revenue',
                  key: 'revenue',
                  align: 'right',
                  width: 150,
                  render: (val) => reportService.formatCurrency(val),
                  sorter: (a, b) => a.revenue - b.revenue
                }
              ]}
            />
          </Card>

          {/* Payment Methods */}
          <Card title="💳 Métodos de Pago" style={{ marginBottom: 24 }}>
            <Table
              dataSource={report.paymentMethods}
              rowKey="PaymentMethod"
              pagination={false}
              size="small"
              columns={[
                {
                  title: 'Método',
                  dataIndex: 'PaymentMethod',
                  key: 'PaymentMethod',
                  render: (code) => getPaymentMethodName(code)
                },
                {
                  title: 'Transacciones',
                  dataIndex: 'transactions',
                  key: 'transactions',
                  align: 'right'
                },
                {
                  title: 'Ingresos',
                  dataIndex: 'revenue',
                  key: 'revenue',
                  align: 'right',
                  render: (val) => reportService.formatCurrency(val)
                },
                {
                  title: 'Ticket Promedio',
                  dataIndex: 'avgTicket',
                  key: 'avgTicket',
                  align: 'right',
                  render: (val) => reportService.formatCurrency(val)
                }
              ]}
            />
          </Card>

          {/* Weather Correlation */}
          <Card 
            title={<><CloudOutlined /> Correlación Clima vs Ventas</>} 
            style={{ marginBottom: 24 }}
          >
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Card 
                  type="inner" 
                  title={<><SunOutlined /> Días Despejados</>}
                  extra={<Tag color="gold">{report.weatherCorrelation.clearDays.days} días</Tag>}
                >
                  <Statistic
                    title="Venta Promedio"
                    value={report.weatherCorrelation.clearDays.days > 0 ? report.weatherCorrelation.clearDays.avgSales : 0}
                    prefix="$"
                    precision={0}
                    valueStyle={{ color: '#faad14' }}
                  />
                  <Divider />
                  <p><strong>Transacciones:</strong> {report.weatherCorrelation.clearDays.transactions || 0}</p>
                  <p><strong>Total:</strong> {reportService.formatCurrency(report.weatherCorrelation.clearDays.totalSales || 0)}</p>
                </Card>
              </Col>
              <Col xs={24} md={12}>
                <Card 
                  type="inner" 
                  title={<><ThunderboltOutlined /> Días Lluviosos</>}
                  extra={<Tag color="blue">{report.weatherCorrelation.rainyDays.days} días</Tag>}
                >
                  <Statistic
                    title="Venta Promedio"
                    value={report.weatherCorrelation.rainyDays.days > 0 ? report.weatherCorrelation.rainyDays.avgSales : 0}
                    prefix="$"
                    precision={0}
                    valueStyle={{ color: '#1890ff' }}
                  />
                  <Divider />
                  <p><strong>Transacciones:</strong> {report.weatherCorrelation.rainyDays.transactions || 0}</p>
                  <p><strong>Total:</strong> {reportService.formatCurrency(report.weatherCorrelation.rainyDays.totalSales || 0)}</p>
                </Card>
              </Col>
            </Row>
            
            <Divider />
            
            <Alert
              message={
                <span>
                  <strong>Impacto del Clima:</strong> {report.weatherCorrelation.impact.interpretation}
                </span>
              }
              description={
                report.weatherCorrelation.rainyDays.days > 0 && report.weatherCorrelation.clearDays.days > 0 ? (
                  <span>
                    Las ventas fueron <strong>{Math.abs(parseFloat(report.weatherCorrelation.impact.percentage)).toFixed(0)}%</strong>{' '}
                    {parseFloat(report.weatherCorrelation.impact.percentage) < 0 ? 
                      <><FallOutlined style={{ color: 'red' }}/> menores</> : 
                      <><RiseOutlined style={{ color: 'green' }}/> mayores</>
                    } en días lluviosos comparado con días despejados
                  </span>
                ) : (
                  <span>No hay suficientes datos para comparar (se necesitan días tanto lluviosos como despejados)</span>
                )
              }
              type={parseFloat(report.weatherCorrelation.impact.percentage) < -20 ? 'warning' : 'info'}
              showIcon
              style={{ marginTop: 16 }}
            />
          </Card>

          {/* Insights */}
          <Card title="💡 Perspectivas" style={{ marginBottom: 24 }}>
            {report.insights.map((insight, index) => (
              <Alert
                key={index}
                message={
                  <span>
                    {insight.icon} <strong>{translateInsightType(insight.type)}</strong>
                    {' - '}
                    <Tag color={getConfidenceColor(insight.confidence)}>
                      {translateConfidenceLevel(insight.level)} ({insight.confidence}% confianza)
                    </Tag>
                  </span>
                }
                description={
                  <div>
                    <p>{translateInsightText(insight.text)}</p>
                    {insight.data && (
                      <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
                        <strong>Datos:</strong> {JSON.stringify(insight.data, null, 2)}
                      </div>
                    )}
                  </div>
                }
                type="info"
                style={{ marginBottom: 12 }}
              />
            ))}
          </Card>

          {/* Recommendations */}
          <Card title="🎯 Recomendaciones" style={{ marginBottom: 24 }}>
            {report.recommendations.map((rec, index) => {
              const translatedRec = translateRecommendation(rec);
              return (
              <Alert
                key={index}
                message={
                  <span>
                    {translatedRec.icon} <strong>{translatedRec.title}</strong>
                    {' '}
                    <Tag color={getPriorityColor(translatedRec.priority)}>
                      {translatePriority(translatedRec.priority)}
                    </Tag>
                    <Tag color={getConfidenceColor(translatedRec.confidence)}>
                      {translatedRec.confidence}% confianza
                    </Tag>
                  </span>
                }
                description={
                  <div>
                    <p><strong>✅ Acción:</strong> {translatedRec.action}</p>
                    <p><strong>📈 Impacto esperado:</strong> {translatedRec.expectedImpact}</p>
                    <p><strong>⏱️ Esfuerzo:</strong> {translatedRec.effort}</p>
                    {translatedRec.data && (
                      <div style={{ marginTop: 8, padding: 8, background: '#f5f5f5', borderRadius: 4 }}>
                        <small><strong>Datos adicionales:</strong></small>
                        <pre style={{ fontSize: 11, margin: 0, marginTop: 4 }}>
                          {JSON.stringify(translatedRec.data, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                }
                type={translatedRec.priority === 'CRITICAL' ? 'error' : translatedRec.priority === 'HIGH' ? 'warning' : 'info'}
                style={{ marginBottom: 12 }}
                showIcon
              />
            );
            })}
          </Card>

          {/* Weather Summary */}
          <Card title="🌤️ Resumen del Clima" style={{ marginBottom: 24 }}>
            <Row gutter={16}>
              <Col span={8}>
                <Statistic 
                  title="Días Totales" 
                  value={report.weather.summary.totalDays} 
                />
              </Col>
              <Col span={8}>
                <Statistic 
                  title="Días Lluviosos" 
                  value={report.weather.summary.rainyDays}
                  suffix={`(${report.weather.summary.rainyPct}%)`}
                />
              </Col>
              <Col span={8}>
                <Statistic 
                  title="Temperatura Promedio" 
                  value={report.weather.summary.avgTemperature}
                  suffix="°C"
                />
              </Col>
            </Row>
          </Card>
        </>
      )}

      {/* Empty State */}
      {!loading && !report && (
        <Card style={{ textAlign: 'center', padding: 60 }}>
          <FileTextOutlined style={{ fontSize: 64, color: '#ccc' }} />
          <h2 style={{ marginTop: 16 }}>Generar Reporte Mensual</h2>
          <p style={{ color: '#666' }}>
            Selecciona un rango de fechas y haz clic en "Generar Reporte" para obtener
            un análisis completo de ventas con correlación climática.
          </p>
        </Card>
      )}
    </div>
  );
};

export default MonthlyReportPage;
